import { PAYMENT_STATUS } from "@elixir/types";
import {
  CellClassParams,
  ICellRendererParams,
  IRowNode,
  RowSpanParams,
} from "ag-grid-community";
import dateFormat from "dateformat";
import PaymentStatusChip from "../molecules/chips/payment-status-chip";
import AmountField from "../organisms/billing-queue/amount-field";
import PaymodeField from "../organisms/billing-queue/paymode-field";
import ReceiptActions from "../organisms/billing-queue/receipt-actions";
import ReceiptHeaderComponent from "../organisms/billing-queue/receipt-header-component";
import ReceiptNumberField from "../organisms/billing-queue/receipt-number-field";
import BillingServiceAutosuggest from "../organisms/billing-queue/service-autosuggest/billing-service-autosuggest";
import SortHeaderComponent from "../organisms/billing-queue/sort-header-component";
import { TagsCellRenderer } from "../organisms/billing-queue/tag-column-cell-renderer";
import TooltipComponent from "../organisms/common/tooltip-component";
import { QueueColDef } from "../stores/billing-store/types";
import { TReceiptPayment } from "../types/appointment";
import { TReceiptEntity } from "../types/billing";
import { getAgeString, getPatientNameText } from "./generic-utils";

export type CellRendererProps = ICellRendererParams<TReceiptEntity>;

const getRowSpanConfig = ({
  currReceipt,
  rowIndex,
  listOfRows = [],
}: {
  currReceipt: TReceiptEntity | undefined;
  rowIndex: number;
  listOfRows: IRowNode<TReceiptEntity>[];
}): {
  spanStatus: "span" | "no_span" | "hide";
  rowSpanNumber: number;
} => {
  if (!currReceipt || listOfRows.length <= 1) {
    return {
      rowSpanNumber: 1,
      spanStatus: "no_span",
    };
  }

  const prevItem = listOfRows[rowIndex - 1];

  if (prevItem?.data?.aid === currReceipt.aid) {
    return {
      rowSpanNumber: 0,
      spanStatus: "hide",
    };
  }

  let lastIndex = rowIndex;

  for (let i = rowIndex + 1; i < listOfRows.length; i++) {
    const rowToCheck = listOfRows[i];
    if (rowToCheck) {
      const { data } = rowToCheck;
      if (data?.aid !== currReceipt.aid) {
        break;
      } else {
        lastIndex = (rowToCheck.rowIndex || i) + 1;
      }
    }
  }

  return {
    rowSpanNumber: lastIndex - rowIndex,
    spanStatus: "span",
  };
};

const getRowSpanNumber = (
  row: CellClassParams<TReceiptEntity, any> | RowSpanParams<TReceiptEntity>
) => {
  const { data, node } = row;
  const { rowIndex: currRowIndex } = node || {};

  if (!data) return 1;

  const listOfRows = row.api.getRenderedNodes();

  const { rowSpanNumber, spanStatus } = getRowSpanConfig({
    rowIndex: currRowIndex as number,
    currReceipt: data,
    listOfRows,
  });

  return rowSpanNumber;
};

export default function getBillingQueueColumnDefinitions(): QueueColDef {
  return [
    {
      colId: "idx",
      field: "idx",
      headerName: "S.No",
      resizable: false,
      headerClass: "uppercase text-11 text-neutral-4 font-700",
      cellRenderer: ({ rowIndex }: CellRendererProps) => {
        return (
          <div className="py-10 flex min-w-full text-neutral-black text-13 items-center h-full">
            {(rowIndex + 1).toString().padStart(2, "0")}
          </div>
        );
      },
      width: 70,
      maxWidth: 70,
    },
    {
      colId: "pt-details",
      headerName: "Patient Details",
      // filter: "agTextColumnFilter",
      // rowSpan: (params) => {
      //   const { data, node } = params;
      //   const { rowIndex: currRowIndex } = node || {};

      //   if (!data) return 1;

      //   const listOfRows = params.api.getRenderedNodes();

      //   const { rowSpanNumber, spanStatus } = getRowSpanConfig({
      //     rowIndex: currRowIndex as number,
      //     currReceipt: data,
      //     listOfRows,
      //   });

      //   return rowSpanNumber;
      // },
      // cellClassRules: {
      //   "cell-span": (params) => getRowSpanNumber(params) > 1,
      // },
      filterParams: {
        textMatcher: (props: {
          filterText: string;
          data: TReceiptEntity;
          filterOption: string;
        }) => {
          const { filterText: typedText, data, filterOption } = props;
          const filterText = typedText.toLowerCase();

          const value = `${data?.patient?.fln}`.toLowerCase();

          if (filterText == null) {
            return false;
          }

          switch (filterOption) {
            case "contains":
              return value.indexOf(filterText) >= 0;
            case "notContains":
              return value.indexOf(filterText) < 0;
            case "equals":
              return value === filterText;
            case "notEqual":
              return value != filterText;
            case "startsWith":
              return value.indexOf(filterText) === 0;
            case "endsWith":
              const index = value.lastIndexOf(filterText);
              return index >= 0 && index === value.length - filterText.length;
            default:
              // should never happen
              console.warn("invalid filter type " + filterOption);
              return false;
          }
        },
      },
      filterValueGetter: ({ data: receiptEntity }) => {
        return `${receiptEntity?.patient.fn && receiptEntity.patient.fn[0]} ${
          receiptEntity?.patient.ln && receiptEntity.patient.ln[0]
        }`.trim();
      },
      menuTabs: ["columnsMenuTab", "filterMenuTab", "generalMenuTab"],
      headerClass: "uppercase text-11 text-neutral-4 font-700",
      sortable: true,
      resizable: true,
      comparator: (_, __, a, b, e) => {
        if (a.data?.patient.fln) {
          return a.data?.patient.fln.localeCompare(b.data?.patient.fln || "");
        }
        return 0;
      },
      headerComponent: SortHeaderComponent,
      cellRenderer: ({ data: receiptEntity }: CellRendererProps) => {
        if (receiptEntity) {
          const { patient } = receiptEntity;
          const { oid, gen, dob } = patient;
          return (
            <div className="py-10 text-neutral-100 text-13 font-400 border-neutral-7 flex items-center h-full overflow-hidden select-text justify-between">
              <div className="flex items-center space-x-12">
                <p>
                  <span>
                    {getPatientNameText(patient)}
                    {gen && ", "}
                  </span>{" "}
                  <span>
                    {oid && gen && `${gen}`}
                    {dob && ", "}
                    {dob
                      ? getAgeString({
                          dob,
                        })
                      : ""}
                  </span>
                </p>
              </div>
              {!!receiptEntity.patient.amount_pending &&
              receiptEntity.patient.amount_pending > 0 ? (
                <TooltipComponent
                  placement="top"
                  title={`Pending Dues: ₹ ${receiptEntity.patient.amount_pending}`}
                >
                  <div className="xl:ml-8 ml-4 min-w-20 min-h-20 h-20 w-20 bg-darwin-status-warning-100 rounded-full flex justify-center items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="1em"
                      viewBox="0 0 320 512"
                      fill="currentColor"
                      className="w-14 h-14 fill-darwin-status-warning-500"
                    >
                      <path d="M0 64C0 46.3 14.3 32 32 32H96h16H288c17.7 0 32 14.3 32 32s-14.3 32-32 32H231.8c9.6 14.4 16.7 30.6 20.7 48H288c17.7 0 32 14.3 32 32s-14.3 32-32 32H252.4c-13.2 58.3-61.9 103.2-122.2 110.9L274.6 422c14.4 10.3 17.7 30.3 7.4 44.6s-30.3 17.7-44.6 7.4L13.4 314C2.1 306-2.7 291.5 1.5 278.2S18.1 256 32 256h80c32.8 0 61-19.7 73.3-48H32c-17.7 0-32-14.3-32-32s14.3-32 32-32H185.3C173 115.7 144.8 96 112 96H96 32C14.3 96 0 81.7 0 64z" />
                    </svg>
                  </div>
                </TooltipComponent>
              ) : null}
            </div>
          );
        }
      },
    },
    {
      colId: "uhid",
      headerName: "UHID",
      resizable: true,
      headerClass: "uppercase text-11 text-neutral-4 font-700",
      hide: true,
      // rowSpan: (params) => {
      //   const { data, node } = params;
      //   const { rowIndex: currRowIndex } = node || {};

      //   if (!data) return 1;

      //   const listOfRows = params.api.getRenderedNodes();

      //   const { rowSpanNumber, spanStatus } = getRowSpanConfig({
      //     rowIndex: currRowIndex as number,
      //     currReceipt: data,
      //     listOfRows,
      //   });

      //   return rowSpanNumber;
      // },
      // cellClassRules: {
      //   "cell-span": (params) => getRowSpanNumber(params) > 1,
      // },
      cellRenderer: ({ data: receiptEntity }: CellRendererProps) => {
        if (receiptEntity) {
          const { uhid } = receiptEntity;

          return (
            <div className="py-10 text-neutral-black text-13 border-neutral-7 break-all flex items-center h-full select-text">
              {uhid || "--"}
            </div>
          );
        }
      },
      maxWidth: 100,
    },
    {
      colId: "contactNum",
      headerName: "Contact Number",
      headerClass: "uppercase text-11 text-neutral-4 font-700",
      resizable: true,
      // rowSpan: (params) => {
      //   const { data, node } = params;
      //   const { rowIndex: currRowIndex } = node || {};

      //   if (!data) return 1;

      //   const listOfRows = params.api.getRenderedNodes();

      //   const { rowSpanNumber, spanStatus } = getRowSpanConfig({
      //     rowIndex: currRowIndex as number,
      //     currReceipt: data,
      //     listOfRows,
      //   });

      //   return rowSpanNumber;
      // },
      // cellClassRules: {
      //   "cell-span": (params) => getRowSpanNumber(params) > 1,
      // },
      cellRenderer: ({ data: receiptEntity }: CellRendererProps) => {
        if (receiptEntity) {
          return (
            <div className="py-10 flex min-w-full text-neutral-black text-13 items-center h-full select-text">
              {receiptEntity.patient.mobile}
            </div>
          );
        }
      },
    },
    {
      colId: "service",
      headerName: "Service",
      headerClass: "uppercase text-11 text-neutral-4 font-700",
      resizable: true,
      suppressKeyboardEvent: () => true,
      cellRenderer: ({ node }: CellRendererProps) => {
        const receiptEntity = node.data;
        if (receiptEntity) {
          const { receipt_id } = receiptEntity;

          const handleServiceSelection = (newReceipt: TReceiptEntity) => {
            node.setData(newReceipt);
          };

          return (
            <div className="z-8 py-10 flex text-neutral-black text-13 items-center h-full">
              <BillingServiceAutosuggest
                receiptEntity={receiptEntity}
                isDisabled={!!receipt_id}
                onServiceSelection={handleServiceSelection}
              />
            </div>
          );
        }
      },
    },
    {
      colId: "amount",
      headerName: "Amount",
      headerClass: "uppercase text-11 text-neutral-4 font-700",
      sortable: true,
      resizable: true,
      suppressKeyboardEvent: () => true,
      comparator: (_, __, a, b, e) => {
        const aAmount = Number(a.data?.receipt_amount);
        const bAmount = Number(b.data?.receipt_amount);

        return (aAmount ? aAmount : 0) - (bAmount ? bAmount : 0);
      },
      headerComponent: SortHeaderComponent,
      cellRenderer: ({ node }: CellRendererProps) => {
        const receiptEntity = node.data;

        if (receiptEntity) {
          const { receipt_id } = receiptEntity;

          const setAmountChange = ({ newAmount }: { newAmount: number }) => {
            const sku = receiptEntity.receiptSkus?.[0];
            const paymentItem = receiptEntity.paymentItems?.[0];

            const newPaymentItem: TReceiptPayment = {
              amount: newAmount || 0,
              paymode: paymentItem?.paymode || "CASH",
            };

            if (sku) {
              sku.amount = newAmount;
              sku.sku_item = {
                ...sku.sku_item,
                price: newAmount,
              };
              node.setData({
                ...receiptEntity,
                receiptSkus: [sku],
                paymentItems: [newPaymentItem],
              });
            } else {
              node.setData({
                ...receiptEntity,
                paymentItems: [newPaymentItem],
                receiptSkus: [
                  {
                    amount: newAmount,
                    discount_amount: 0,
                    discount_type: "AMOUNT",
                    discount_value: 0,
                    net_amount: newAmount,
                    quantity: 1,
                    sku_item: {
                      price: newAmount,
                      service_id: null,
                      service_name: "",
                    },
                  },
                ],
              });
            }
          };

          return (
            <div className="py-10 flex min-w-full text-neutral-black text-13 items-center h-full">
              <AmountField
                receiptEntity={receiptEntity}
                isDisabled={!!receipt_id}
                handleAmountChange={setAmountChange}
              />
            </div>
          );
        }
      },
      maxWidth: 150,
    },
    {
      colId: "paymode",
      headerName: "Paymode",
      headerClass: "uppercase text-11 text-neutral-4 font-700 bg-neutral-0",
      resizable: true,
      cellRenderer: ({ node }: CellRendererProps) => {
        const receiptEntity = node.data;
        if (receiptEntity) {
          const { receipt_id } = receiptEntity;

          const handlePaymodeSelect = (newPaymode: TReceiptPayment) => {
            node.setData({
              ...receiptEntity,
              paymentItems: [newPaymode],
            });
          };

          return (
            <div className="py-10 flex min-w-full text-neutral-black text-13 items-center h-full">
              <PaymodeField
                receiptEntity={receiptEntity}
                isDisabled={!!receipt_id}
                onPaymodeSelect={handlePaymodeSelect}
              />
            </div>
          );
        }
      },
      maxWidth: 180,
    },
    // TODO: Add Receipt number to filter
    {
      colId: "receipt_number",
      headerName: "Receipt #",
      headerClass: "uppercase text-11 text-neutral-4 font-700",
      resizable: true,
      headerComponent: ReceiptHeaderComponent,
      cellRenderer: ({ data: receiptEntity, rowIndex }: CellRendererProps) => {
        if (receiptEntity) {
          const { receipt_number, business_receipt_number } = receiptEntity;

          return (
            <div className="py-10 text-neutral-black text-13 border-neutral-7 break-all flex items-center h-full">
              <ReceiptNumberField
                receiptNumber={business_receipt_number || receipt_number || ""}
                isFirst={rowIndex === 0}
              />
            </div>
          );
        }
      },
      maxWidth: 180,
    },
    {
      colId: "tags",
      headerName: "Tags",
      headerClass: "uppercase text-11 text-neutral-4 font-700 ml-8",
      resizable: true,
      cellRenderer: TagsCellRenderer,
      maxWidth: 180,
      hide: true,
    },
    {
      colId: "visitedOn",
      headerName: "Visited On",
      headerClass: "uppercase text-11 text-neutral-4 font-700",
      resizable: true,
      sortable: true,
      comparator: (_, __, a, b, e) => {
        const sortValue =
          (a.data?.visited_on ? new Date(a.data?.visited_on).getTime() : 0) -
          (b.data?.visited_on ? new Date(b.data.visited_on).getTime() : 0);
        return sortValue;
      },
      headerComponent: SortHeaderComponent,
      cellRenderer: ({ data: receiptEntity }: CellRendererProps) => {
        if (receiptEntity) {
          const { visited_on } = receiptEntity;
          return (
            <div className="py-10 flex min-w-full text-neutral-black text-13 items-center h-full select-text">
              {visited_on ? (
                <div>
                  <div className="font-400">
                    {dateFormat(new Date(visited_on), "dd mmm'yy")}
                  </div>
                  <div className="text-11 font-400 text-darwin-neutral-500">
                    {dateFormat(new Date(visited_on), "hh:MM TT")}
                  </div>
                </div>
              ) : (
                "-"
              )}
            </div>
          );
        }
      },
      maxWidth: 160,
      hide: true,
    },
    {
      colId: "pay-status",
      headerName: "Status",
      headerClass: "uppercase text-11 text-neutral-4 font-700",
      resizable: true,
      cellRenderer: ({ data: receiptEntity }: CellRendererProps) => {
        if (receiptEntity) {
          const { payment_status } = receiptEntity;
          return (
            <div className="py-10 flex min-w-full text-neutral-black text-13 items-center h-full">
              <PaymentStatusChip status={payment_status} />
            </div>
          );
        }
      },
      maxWidth: 150,
      hide: true,
    },
    {
      colId: "receiptEntities-date",
      headerName: "Trx. Date",
      headerClass: "uppercase text-11 text-neutral-4 font-700",
      sortable: true,
      resizable: true,
      hide: true,
      comparator: (_, __, a, b, e) => {
        const sortValue =
          (a.data?.transaction_date
            ? new Date(a.data?.transaction_date).getTime()
            : 0) -
          (b.data?.transaction_date
            ? new Date(b.data.transaction_date).getTime()
            : 0);
        return sortValue;
      },
      headerComponent: SortHeaderComponent,
      cellRenderer: ({ data: receiptEntity }: CellRendererProps) => {
        if (receiptEntity) {
          const { transaction_date } = receiptEntity;
          return (
            <div className="py-10 flex min-w-full text-neutral-black text-13 items-center h-full select-text">
              {transaction_date ? (
                <div>
                  <div className="font-400">
                    {dateFormat(new Date(transaction_date), "dd mmm'yy")}
                  </div>
                  <div className="text-11 font-400 text-darwin-neutral-500">
                    {dateFormat(new Date(transaction_date), "hh:MM TT")}
                  </div>
                </div>
              ) : (
                "-"
              )}
            </div>
          );
        }
      },
    },
    {
      colId: "actions",
      headerName: "Actions",
      headerClass: "uppercase text-11 text-neutral-4 font-700",
      pinned: "right",
      suppressMovable: true,
      resizable: true,
      width: 288,
      cellRenderer: ({ data: receiptEntity, ...rest }: CellRendererProps) => {
        if (receiptEntity) {
          const { receiptSkus, payment_status } = receiptEntity;
          const { sku_item } = receiptSkus?.[0] || {};
          const { service_name } = sku_item || {};

          return (
            <div className="py-4 flex items-center h-full">
              <ReceiptActions
                receiptEntity={receiptEntity}
                isPaid={payment_status !== PAYMENT_STATUS.UNBILLED}
                isQuickReceiptDisabled={!service_name}
              />
            </div>
          );
        }
      },
    },
    {
      colId: "receipt-remarks",
      headerName: "Remarks",
      headerClass: "uppercase text-11 text-neutral-4 font-700",
      sortable: true,
      resizable: true,
      hide: true,
      cellRenderer: ({ data: receiptEntity, ...rest }: CellRendererProps) => {
        if (receiptEntity) {
          const { remarks } = receiptEntity;

          return (
            <div className="py-10 flex min-w-full text-neutral-black text-13 items-center h-full select-text">
              {remarks}
            </div>
          );
        }
      },
    },
  ];
}
